import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage as FM } from 'react-intl';
import queryString from 'query-string';
import { MoneyCell, PageContentPreloader, NoItemsPanel, DefaultButton } from 'common/components';
import noPaymentsImage from 'common/images/noPayments.svg';
import { staticStyles } from './style';

// ─── Money helper ───────────────────────────────────────────────────────────
const ZERO_MONEY = { amount: '0.00', currency: 'USD' };

const renderMoney = moneyObj => {
  if (!moneyObj) return <MoneyCell heightMaxContent value={ZERO_MONEY} />;
  return <MoneyCell heightMaxContent value={moneyObj} />;
};

const renderLots = lots => {
  if (lots === null || lots === undefined) return <span>0</span>;
  return <span>{lots}</span>;
};

// ─── User Row ───────────────────────────────────────────────────────────────
const UserRow = ({ node, depth, onRowClick }) => {
  const indentPx = depth * 24;
  return (
    <div
      role="button"
      tabIndex={0}
      className="PartnershipIBTreeTable__Row PartnershipIBTreeTable__Row--user"
      style={{ paddingLeft: `${16 + indentPx}px` }}
      onClick={() => onRowClick && onRowClick(node.id)}
      onKeyDown={e => {
        if (e.key === 'Enter' && onRowClick) onRowClick(node.id);
      }}>
      <div className="PartnershipIBTreeTable__Cell PartnershipIBTreeTable__Cell--name">
        <span>{node.fullName}</span>
      </div>
      <div className="PartnershipIBTreeTable__Cell">
        <span>{node.dateJoined || '---'}</span>
      </div>
      <div className="PartnershipIBTreeTable__Cell">{renderMoney(node.totalIn)}</div>
      <div className="PartnershipIBTreeTable__Cell">{renderMoney(node.totalOut)}</div>
      <div className="PartnershipIBTreeTable__Cell">{renderMoney(node.totalDiff)}</div>
      <div className="PartnershipIBTreeTable__Cell">{renderLots(node.totalLots)}</div>
    </div>
  );
};

UserRow.propTypes = {
  node: PropTypes.object.isRequired,
  depth: PropTypes.number.isRequired,
  onRowClick: PropTypes.func,
};

UserRow.defaultProps = {
  onRowClick: null,
};

// ─── IB Summary Row ─────────────────────────────────────────────────────────
const IBSummaryRow = ({ node, depth, defaultOpen = false, onRowClick }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const indentPx = depth * 24;
  const children = Array.isArray(node.children) ? node.children : [];
  const hasChildren = children.length > 0;
  const ibLevel = node.ib_level || 1;

  return (
    <>
      {/* IB summary row */}
      <div
        role="button"
        tabIndex={0}
        className="PartnershipIBTreeTable__Row PartnershipIBTreeTable__Row--ib"
        style={{ paddingLeft: `${16 + indentPx}px` }}
        onClick={() => {
          if (hasChildren) setIsOpen(prev => !prev);
        }}
        onKeyDown={e => {
          if (e.key === 'Enter' && hasChildren) setIsOpen(prev => !prev);
        }}>
        <div className="PartnershipIBTreeTable__Cell PartnershipIBTreeTable__Cell--name">
          {hasChildren && (
            <button
              type="button"
              className={`PartnershipIBTreeTable__ExpandBtn${isOpen ? ' PartnershipIBTreeTable__ExpandBtn--open' : ''}`}
              onClick={e => {
                e.stopPropagation();
                setIsOpen(prev => !prev);
              }}>
              ›
            </button>
          )}
          <span>{node.fullName}</span>
          <span className="PartnershipIBTreeTable__IBLabel">IB Level {ibLevel}</span>
        </div>
        <div className="PartnershipIBTreeTable__Cell">
          <span>---</span>
        </div>
        <div className="PartnershipIBTreeTable__Cell">{renderMoney(node.totalIn)}</div>
        <div className="PartnershipIBTreeTable__Cell">{renderMoney(node.totalOut)}</div>
        <div className="PartnershipIBTreeTable__Cell">{renderMoney(node.totalDiff)}</div>
        <div className="PartnershipIBTreeTable__Cell">{renderLots(node.totalLots)}</div>
      </div>

      {/* children (only rendered when open) */}
      {isOpen &&
        children.map((child, idx) =>
          child.node_type === 'ib' ? (
            <IBSummaryRow
              key={`ib-${child.id}-${idx}`}
              node={child}
              depth={depth + 1}
              defaultOpen={defaultOpen}
              onRowClick={onRowClick}
            />
          ) : (
            <UserRow key={`user-${child.id}-${idx}`} node={child} depth={depth + 1} onRowClick={onRowClick} />
          )
        )}
    </>
  );
};

IBSummaryRow.propTypes = {
  node: PropTypes.object.isRequired,
  depth: PropTypes.number.isRequired,
  defaultOpen: PropTypes.bool,
  onRowClick: PropTypes.func,
};

IBSummaryRow.defaultProps = {
  defaultOpen: false,
  onRowClick: null,
};

// ─── Main Table Component ───────────────────────────────────────────────────
const PartnershipIBTreeTable = ({ ibTree, ibTreeIsLoaded, onRowClick, location, history }) => {
  const results = ibTree?.results || [];
  const totalCount = ibTree?.count || 0;

  const parsedQuery = useMemo(() => queryString.parse(location?.search || ''), [location?.search]);
  const currentPage = Number(parsedQuery.page) || 1;
  const pageSize = Number(parsedQuery.page_size) || 10;
  const pageCount = useMemo(() => Math.ceil(totalCount / pageSize) || 0, [totalCount, pageSize]);
  const hasSearch = !!parsedQuery.search;

  const goToPage = page => {
    const next = queryString.stringify({ ...parsedQuery, page, page_size: pageSize });
    history.replace({ search: next });
  };

  const handleInputChange = e => {
    const v = e.target.value;
    if (v === '' || (Number(v) >= 1 && Number(v) <= pageCount)) {
      // allow typing
    }
  };

  const handleInputBlur = e => {
    const v = Number(e.target.value);
    if (v >= 1 && v <= pageCount && v !== currentPage) {
      goToPage(v);
    }
  };

  const handleRowClick = id => {
    if (onRowClick) onRowClick(id);
  };

  return (
    <div className="PartnershipIBTreeTable">
      <PageContentPreloader ready={ibTreeIsLoaded} type="crmTable" noPadding>
        <div className="PartnershipIBTreeTable__wrapper">
          {/* Header */}
          <div className="PartnershipIBTreeTable__HeaderRow">
            <div className="PartnershipIBTreeTable__HeaderCell">
              <span className="PartnershipIBTreeTable__HeaderTop">
                <FM id="justName">Name</FM>
              </span>
            </div>
            <div className="PartnershipIBTreeTable__HeaderCell">
              <span className="PartnershipIBTreeTable__HeaderTop">
                <FM id="justDateJoined">Date Joined</FM>
              </span>
            </div>
            <div className="PartnershipIBTreeTable__HeaderCell">
              {ibTree?.total_in_sum && (
                <span className="PartnershipIBTreeTable__HeaderTop">{renderMoney(ibTree.total_in_sum)}</span>
              )}
              <span className="PartnershipIBTreeTable__HeaderLabel">
                <FM id="justIn">In</FM>
              </span>
            </div>
            <div className="PartnershipIBTreeTable__HeaderCell">
              {ibTree?.total_out_sum && (
                <span className="PartnershipIBTreeTable__HeaderTop">{renderMoney(ibTree.total_out_sum)}</span>
              )}
              <span className="PartnershipIBTreeTable__HeaderLabel">
                <FM id="justOut">Out</FM>
              </span>
            </div>
            <div className="PartnershipIBTreeTable__HeaderCell">
              {ibTree?.total_diff_sum && (
                <span className="PartnershipIBTreeTable__HeaderTop">{renderMoney(ibTree.total_diff_sum)}</span>
              )}
              <span className="PartnershipIBTreeTable__HeaderLabel">In/Out</span>
            </div>
            <div className="PartnershipIBTreeTable__HeaderCell">
              {ibTree?.total_lots !== undefined && ibTree?.total_lots !== null && (
                <span className="PartnershipIBTreeTable__HeaderTop">{ibTree.total_lots}</span>
              )}
              <span className="PartnershipIBTreeTable__HeaderLabel">Total Lots</span>
            </div>
          </div>

          {/* Body */}
          {!results.length ? (
            <NoItemsPanel
              icon={noPaymentsImage}
              containerMaxHeight
              captionSlug="partnershipNoClientsCaptionMatchingTheParameters"
            />
          ) : (
            <>
              <div className="PartnershipIBTreeTable__Tbody">
                {results.map((node, idx) =>
                  node.node_type === 'ib' ? (
                    <IBSummaryRow
                      key={`ib-${node.id}-${idx}`}
                      node={node}
                      depth={0}
                      defaultOpen={hasSearch}
                      onRowClick={handleRowClick}
                    />
                  ) : (
                    <UserRow key={`user-${node.id}-${idx}`} node={node} depth={0} onRowClick={handleRowClick} />
                  )
                )}
              </div>

              {/* Pagination */}
              {pageCount > 0 && (
                <div className="PartnershipIBTreeTable__Pagination">
                  <div className="PartnershipIBTreeTable__PageInfo">
                    <FM id="justPage">{txt => <span>{txt}</span>}</FM>
                    <input
                      className="PartnershipIBTreeTable__PageInput"
                      type="number"
                      min={1}
                      max={pageCount}
                      defaultValue={currentPage}
                      key={currentPage}
                      onChange={handleInputChange}
                      onBlur={handleInputBlur}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleInputBlur(e);
                      }}
                    />
                    <FM id="justOf">{txt => <span>{txt}</span>}</FM>
                    <span>{pageCount}</span>
                  </div>
                  <div className="PartnershipIBTreeTable__PageBtns">
                    <DefaultButton
                      textId="justPrevious"
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage <= 1}
                      previous
                    />
                    <DefaultButton
                      textId="justNext"
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage >= pageCount}
                      next
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </PageContentPreloader>
      <style jsx global>
        {staticStyles}
      </style>
    </div>
  );
};

PartnershipIBTreeTable.propTypes = {
  ibTree: PropTypes.object,
  ibTreeIsLoaded: PropTypes.bool.isRequired,
  onRowClick: PropTypes.func,
  location: PropTypes.object,
  history: PropTypes.object,
};

PartnershipIBTreeTable.defaultProps = {
  ibTree: { results: [], count: 0 },
  onRowClick: null,
  location: null,
  history: null,
};

export default PartnershipIBTreeTable;
